import { __decorate, __metadata, __param } from "tslib";
import { inject, injectable } from 'inversify';
import { Symbols } from '@/symbols';
import { backofficeUrlBase } from '@/config/envConfig';
import { CreditsPaths } from './CreditsApi.type';
/**
 * 公司积分充值等：直连 Backoffice Service（与 company-credits 控制器一致）。
 * Base：`backofficeUrlBase` + `/backoffice`
 */
let CreditsApi = class CreditsApi {
    constructor(http) {
        this.http = http;
    }
    url(path) {
        const base = `${backofficeUrlBase}/backoffice`.replace(/\/$/, '');
        const p = path.startsWith('/') ? path : `/${path}`;
        return `${base}${p}`;
    }
    makeConsumptionQueryString(query = {}) {
        const params = new URLSearchParams();
        if (query.page_no != null)
            params.set('page_no', String(query.page_no));
        if (query.page_size != null)
            params.set('page_size', String(query.page_size));
        if (query.cursor_created_at != null)
            params.set('cursor_created_at', String(query.cursor_created_at));
        if (query.cursor_id)
            params.set('cursor_id', query.cursor_id);
        if (query.consume_at_start != null)
            params.set('consume_at_start', String(query.consume_at_start));
        if (query.consume_at_end != null)
            params.set('consume_at_end', String(query.consume_at_end));
        if (query.tenant_user_id)
            params.set('tenant_user_id', query.tenant_user_id);
        if (query.status)
            params.set('status', query.status);
        if (query.feature_name)
            params.set('feature_name', query.feature_name);
        if (query.job_id)
            params.set('job_id', query.job_id);
        if (query.charge_mode)
            params.set('charge_mode', query.charge_mode);
        if (query.contain_item != null)
            params.set('contain_item', String(query.contain_item));
        if (query.credits_group_id)
            params.set('credits_group_id', query.credits_group_id);
        return params.toString();
    }
    getBlob(url) {
        return this.http.request({
            url,
            method: 'get',
            responseType: 'blob',
            rawResponse: true,
        });
    }
    /** POST /company-credits/recharge — 公司积分充值 */
    postCompanyCreditsRecharge(data) {
        const url = this.url(CreditsPaths.companyRecharge);
        return this.http.post({
            url,
            data,
        });
    }
    /** POST /company-credits/recharge — 通过 related_recharge_record_id 发起积分冲正 */
    postCompanyCreditsRechargeOffset(data) {
        return this.postCompanyCreditsRecharge(data);
    }
    /** PUT /company-credits/recharge — 编辑公司积分充值记录有效期 */
    putCompanyCreditsRecharge(data) {
        const url = this.url(CreditsPaths.companyRecharge);
        return this.http.put({
            url,
            data,
        });
    }
    /** GET /company-credits/:id/recharge-detail — 充值明细与积分汇总 */
    getCompanyCreditsRechargeDetail(companyId) {
        const path = CreditsPaths.companyRechargeDetail.replace(':company_id', encodeURIComponent(companyId));
        const url = this.url(path);
        return this.http.get({ url });
    }
    companyGroupPath(path, companyId, groupId) {
        return path
            .replace(':company_id', encodeURIComponent(companyId))
            .replace(':group_id', encodeURIComponent(groupId || ''));
    }
    getCompanyCreditsGroups(companyId, includeDeleted = false) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroups, companyId);
        const params = new URLSearchParams({ includeDeleted: String(includeDeleted) });
        return this.http.get({ url: `${this.url(path)}?${params}` });
    }
    getCompanyCreditsGroup(companyId, groupId) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroup, companyId, groupId);
        return this.http.get({ url: this.url(path) });
    }
    postCompanyCreditsGroup(companyId, data) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroups, companyId);
        return this.http.post({ url: this.url(path), data });
    }
    putCompanyCreditsGroup(companyId, groupId, data) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroup, companyId, groupId);
        return this.http.put({ url: this.url(path), data });
    }
    deleteCompanyCreditsGroup(companyId, groupId) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroup, companyId, groupId);
        return this.http.delete({ url: this.url(path) });
    }
    getCompanyCreditsGroupUsers(companyId) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroupUsers, companyId);
        return this.http.get({ url: this.url(path) });
    }
    postCompanyCreditsGroupAllocations(companyId, groupId, data) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroupAllocations, companyId, groupId);
        return this.http.post({ url: this.url(path), data });
    }
    postCompanyCreditsGroupAllocationRelease(companyId, groupId, data) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroupAllocationsRelease, companyId, groupId);
        return this.http.post({ url: this.url(path), data });
    }
    getCompanyCreditsGroupAllocationDetails(companyId, groupId) {
        const path = this.companyGroupPath(CreditsPaths.companyCreditGroupAllocationDetails, companyId, groupId);
        return this.http.get({ url: this.url(path) });
    }
    /** GET /company-credits/:id/consumption-detail — 消耗明细与总消耗 */
    getCompanyCreditsConsumptionDetail(companyId, query = {}) {
        const path = CreditsPaths.companyConsumptionDetail.replace(':company_id', encodeURIComponent(companyId));
        const params = this.makeConsumptionQueryString(query);
        const url = params ? `${this.url(path)}?${params}` : this.url(path);
        return this.http.get({ url });
    }
    /** GET /company-credits/:id/consumption-detail/download */
    getCompanyCreditsConsumptionDetailDownload(companyId, query) {
        const path = CreditsPaths.companyConsumptionDetailDownload.replace(':company_id', encodeURIComponent(companyId));
        const params = this.makeConsumptionQueryString(query);
        return this.getBlob(params ? `${this.url(path)}?${params}` : this.url(path));
    }
    /** GET /tenant-user-credits/:tenant_user_id/recharge-detail */
    getTenantUserCreditsRechargeDetail(tenantUserId) {
        const path = CreditsPaths.tenantUserRechargeDetail.replace(':tenant_user_id', encodeURIComponent(tenantUserId));
        return this.http.get({ url: this.url(path) });
    }
    /** GET /tenant-user-credits/:tenant_user_id/consumption-detail */
    getTenantUserCreditsConsumptionDetail(tenantUserId, query = {}) {
        const path = CreditsPaths.tenantUserConsumptionDetail.replace(':tenant_user_id', encodeURIComponent(tenantUserId));
        const params = this.makeConsumptionQueryString(query);
        const url = params ? `${this.url(path)}?${params}` : this.url(path);
        return this.http.get({ url });
    }
    /** GET /tenant-user-credits/:tenant_user_id/consumption-detail/download */
    getTenantUserCreditsConsumptionDetailDownload(tenantUserId, query) {
        const path = CreditsPaths.tenantUserConsumptionDetailDownload.replace(':tenant_user_id', encodeURIComponent(tenantUserId));
        const params = this.makeConsumptionQueryString(query);
        return this.getBlob(params ? `${this.url(path)}?${params}` : this.url(path));
    }
    /** PUT /tenant-users/:tenant_user_id/daily-credit-limit */
    putTenantUserDailyCreditLimit(tenantUserId, data) {
        const path = CreditsPaths.tenantUserDailyCreditLimit.replace(':tenant_user_id', encodeURIComponent(tenantUserId));
        return this.http.put({
            url: this.url(path),
            data,
        });
    }
};
CreditsApi = __decorate([
    injectable(),
    __param(0, inject(Symbols.BoHttp)),
    __metadata("design:paramtypes", [Object])
], CreditsApi);
export { CreditsApi };
